import { useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { fileUrl, runEngine, enhanceImage, confirmTriage } from "@/lib/api";
import { EngineFindings } from "@/components/EngineFindings";
import { ACTION_META } from "@/lib/engineMeta";
import { Loader2, Sparkles, ZoomIn, ZoomOut, MapPin, X, Wand2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { key: "file_id", label: "Original" },
  { key: "enhanced_file_id", label: "Enhanced" },
  { key: "pattern_file_id", label: "Patterns" },
];

export function EvidenceViewer({ evidence, index, open, onOpenChange, onUpdated, caseId }) {
  const [tab, setTab] = useState("file_id");
  const [zoom, setZoom] = useState(1);
  const [pins, setPins] = useState([]);
  const [running, setRunning] = useState(null);
  const [enhancing, setEnhancing] = useState(false);
  const [confirming, setConfirming] = useState(false);

  if (!evidence) return null;
  const pendingReview = evidence.ai_drafted && evidence.triage_status !== "confirmed" && evidence.triage_status !== "pending";
  const confirmDraft = async () => {
    setConfirming(true);
    try {
      await confirmTriage(caseId, evidence.id);
      toast.success("Triage confirmed — record accepted into the case file");
      await onUpdated?.();
    } catch { toast.error("Could not confirm triage"); }
    finally { setConfirming(false); }
  };
  const hasImage = !!evidence.file_id;
  // A PDF/document has a file_id but is NOT an image — show its extracted text, not a
  // broken <img>. Only treat as an image when it's a photo or carries no readable text.
  const isImage = hasImage && (evidence.kind === "photo" || !((evidence.content || "").trim()));
  const findings = evidence.engine_findings || {};

  const actions = [];
  if (isImage) actions.push("image", "ocr", "handwriting");
  if ((evidence.content || "").trim()) actions.push("cipher", "linguistic");

  // Always surface EVERY finding that exists (ordered), regardless of the current derived
  // actions, so results never silently disappear when file/content state changes.
  const ORDER = ["image", "ocr", "handwriting", "cipher", "linguistic"];
  const findingKeys = Object.keys(findings).sort(
    (a, b) => (ORDER.indexOf(a) + 1 || 99) - (ORDER.indexOf(b) + 1 || 99)
  );

  const activeFileId = evidence[tab];

  const addPin = (e) => {
    if (!hasImage) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setPins((p) => [...p, { x, y }]);
  };

  const enhance = async () => {
    setEnhancing(true);
    try {
      await enhanceImage(evidence.id);
      toast.success("Enhancement complete — Enhanced & Patterns views ready");
      await onUpdated?.();
      setTab("enhanced_file_id");
    } catch { toast.error("Enhancement failed"); }
    finally { setEnhancing(false); }
  };

  const run = async (action) => {
    setRunning(action);
    try {
      await runEngine(evidence.id, action);
      toast.success(`${ACTION_META[action].name} complete`);
      await onUpdated?.();
    } catch { toast.error(`${ACTION_META[action].name} could not complete`); }
    finally { setRunning(null); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-monk-panel border-white/10 text-white max-w-[1200px] w-[95vw] p-0 gap-0 overflow-hidden" data-testid="evidence-viewer">
        <DialogTitle className="sr-only">Evidence Board — {evidence.label}</DialogTitle>
        <DialogDescription className="sr-only">Forensic evidence viewer with contextual inquiry engines and image enhancement.</DialogDescription>
        <div className="grid grid-cols-1 lg:grid-cols-5 max-h-[86vh]">
          {/* Center Stage — Section 14 */}
          <div className="lg:col-span-3 bg-black border-r border-white/10 flex flex-col">
            <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2">
              <span className="font-mono text-[11px] text-monk-blue">E{index + 1}</span>
              <span className="overline">{evidence.kind}</span>
              <span className="hard-data text-sm font-medium ml-1 truncate">{evidence.label}</span>
            </div>

            {isImage ? (
              <>
                <div className="flex items-center gap-1 px-4 py-2 border-b border-white/10">
                  {TABS.map((t) => {
                    const available = !!evidence[t.key];
                    return (
                      <button key={t.key} disabled={!available} onClick={() => setTab(t.key)} data-testid={`view-tab-${t.label.toLowerCase()}`}
                        className={`text-[11px] font-mono px-3 py-1.5 rounded-sm border transition-colors ${tab === t.key ? "border-monk-blue text-monk-blue bg-monk-blue/10" : available ? "border-white/15 text-neutral-400 hover:text-white" : "border-white/5 text-neutral-700 cursor-not-allowed"}`}>
                        {t.label.toUpperCase()}
                      </button>
                    );
                  })}
                  <div className="ml-auto flex items-center gap-1">
                    <button onClick={() => setZoom((z) => Math.max(1, +(z - 0.25).toFixed(2)))} className="w-7 h-7 rounded-sm border border-white/15 flex items-center justify-center text-neutral-400 hover:text-white"><ZoomOut className="w-3.5 h-3.5" /></button>
                    <span className="font-mono text-[10px] text-neutral-500 w-9 text-center">{zoom.toFixed(2)}x</span>
                    <button onClick={() => setZoom((z) => Math.min(4, +(z + 0.25).toFixed(2)))} data-testid="zoom-in" className="w-7 h-7 rounded-sm border border-white/15 flex items-center justify-center text-neutral-400 hover:text-white"><ZoomIn className="w-3.5 h-3.5" /></button>
                    {pins.length > 0 && <button onClick={() => setPins([])} className="w-7 h-7 rounded-sm border border-white/15 flex items-center justify-center text-neutral-400 hover:text-monk-critical"><X className="w-3.5 h-3.5" /></button>}
                  </div>
                </div>
                <div className="relative flex-1 overflow-auto grid-noise flex items-center justify-center p-4">
                  <div className="relative" onClick={addPin} style={{ cursor: "crosshair" }}>
                    {activeFileId ? (
                      <img src={fileUrl(activeFileId)} alt={evidence.label} data-testid="evidence-image"
                        style={{ transform: `scale(${zoom})`, transformOrigin: "center", transition: "transform 0.2s ease" }}
                        className="max-h-[58vh] w-auto select-none" draggable={false} />
                    ) : <div className="text-neutral-600 text-sm p-12">View not generated yet.</div>}
                    {pins.map((p, i) => (
                      <div key={i} className="absolute -translate-x-1/2 -translate-y-full pointer-events-none" style={{ left: `${p.x}%`, top: `${p.y}%` }}>
                        <MapPin className="w-5 h-5 text-monk-critical drop-shadow" fill="#FF3B30" />
                        <span className="absolute -top-1 left-1/2 -translate-x-1/2 text-[8px] font-mono text-white">{i + 1}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="px-4 py-2 border-t border-white/10 flex items-center gap-3">
                  <button onClick={enhance} disabled={enhancing} data-testid="enhance-btn"
                    className="flex items-center gap-2 text-[12px] bg-monk-blue text-white rounded-sm px-3 py-1.5 hover:bg-monk-blue/90 disabled:opacity-60 transition-colors">
                    {enhancing ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Enhancing…</> : <><Wand2 className="w-3.5 h-3.5" /> Enhance & Extract Patterns</>}
                  </button>
                  <span className="hunch-data text-[11px]">Click the image to drop inquiry pins.</span>
                </div>
              </>
            ) : (
              <div className="flex-1 overflow-auto p-5">
                <div className="overline mb-2">Document Text</div>
                <pre className="whitespace-pre-wrap font-mono text-[12px] text-neutral-200 leading-relaxed">{evidence.content}</pre>
              </div>
            )}
          </div>

          {/* Forensic engines panel */}
          <div className="lg:col-span-2 flex flex-col overflow-hidden">
            <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-monk-blue" /><span className="overline">Contextual Inquiry · Engines</span>
            </div>

            {evidence.ai_drafted && (
              <div className="px-4 py-3 border-b border-white/10 bg-black/30" data-testid="triage-draft-banner">
                <div className="flex items-center gap-2 mb-1.5">
                  <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded-sm border ${pendingReview ? "text-monk-warn border-monk-warn/40 bg-monk-warn/10" : "text-emerald-400 border-emerald-500/40 bg-emerald-500/10"}`}>
                    {pendingReview ? "AI TRIAGE DRAFT · PENDING REVIEW" : "AI TRIAGE DRAFT · REVIEWED"}
                  </span>
                </div>
                {evidence.notes && <p className="text-[12px] text-neutral-300 leading-relaxed mb-2 whitespace-pre-wrap" data-testid="triage-draft-notes">{evidence.notes}</p>}
                {pendingReview && (
                  <button onClick={confirmDraft} disabled={confirming} data-testid="confirm-triage-btn"
                    className="flex items-center gap-1.5 text-[11px] bg-emerald-600/80 hover:bg-emerald-600 text-white rounded-sm px-2.5 py-1.5 disabled:opacity-60 transition-colors">
                    {confirming ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />} Confirm Triage
                  </button>
                )}
              </div>
            )}
            <div className="p-4 flex flex-wrap gap-2 border-b border-white/10">
              {actions.map((a) => (
                <button key={a} onClick={() => run(a)} disabled={running === a} data-testid={`run-${a}`}
                  className="text-[12px] border border-white/15 text-neutral-300 rounded-sm px-3 py-1.5 hover:text-white hover:border-monk-blue/50 disabled:opacity-60 transition-colors flex items-center gap-1.5">
                  {running === a ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}{ACTION_META[a].name}
                </button>
              ))}
            </div>
            <div className="flex-1 overflow-auto p-4 space-y-4">
              {Object.keys(findings).length === 0 && !running && (
                <p className="hunch-data text-[13px]">No engine has examined this evidence yet. Route it to a specialist above.</p>
              )}
              {findingKeys.map((a) => (
                <EngineFindings key={a} action={a} name={ACTION_META[a]?.name || a} result={findings[a]} />
              ))}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
